import {
  Avatar,
  Box,
  Button,
  Chip,
  InputAdornment,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Icon } from '@iconify/react';
import useResponsive from '../../../../../Hooks/useResponsive';
import AddEvaluationManagerFormLanding from '../../../../appraisal/evaluation/addEvaluationSelf/AddEvaluationManagerFormLanding';
import PublishReport from './PublishReport';

const ManageAppraisalCard = ({ open, cycleId, cycleName }) => {
  const smUp = useResponsive('up', 'sm');
  const mdUp = useResponsive('up', 'md');
  const [selected, setSelected] = useState(null);
  // Text typed in the search box. Used to filter the managers (appraisers) list.
  const [searchQuery, setSearchQuery] = useState('');
  const { manageAppraisalData } = useSelector((state) => state.appraisal);

  useEffect(() => {
    if (!open) {
      setSelected(null);
      // Clear the search text when the popup closes so it starts fresh next time.
      setSearchQuery('');
    }
  }, [open]);

  // Filter the managers by the typed name (case-insensitive). Pure front-end filter
  // over the already-loaded list, so it does not call the backend.
  const query = searchQuery.trim().toLowerCase();
  const filteredAppraisers = query
    ? (manageAppraisalData || []).filter((item) => (item.appraiserName || '').toLowerCase().includes(query))
    : manageAppraisalData || [];

  // When manageAppraisalData updates (after publish/unpublish), refresh the selected item
  useEffect(() => {
    if (selected && manageAppraisalData?.length > 0) {
      const updated = manageAppraisalData.find((i) => i.userId === selected.userId);
      if (updated) {
        setSelected(updated);
      }
    }
  }, [manageAppraisalData]);

  return (
    <Box sx={{ display: 'flex', gap: 1 }}>
      <Box sx={{ width: '30%', height: '85vh', display: 'flex', flexDirection: 'column' }}>
        {/* Search box stays fixed at the top-left; the managers list scrolls below it */}
        <TextField
          size="small"
          placeholder="Search manager by name"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          fullWidth
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Icon icon="mdi:magnify" width="20" height="20" />
              </InputAdornment>
            ),
          }}
        />

        {/* Scrollable list of managers (filtered by the search box above) */}
        <Box sx={{ flex: 1, overflow: 'auto', mt: 1 }}>
          {filteredAppraisers.length === 0 ? (
            <Typography variant="body2" sx={{ textAlign: 'center', mt: 2, color: 'text.secondary' }}>
              No managers match "{searchQuery}"
            </Typography>
          ) : (
            filteredAppraisers.map((item) => {
              // Work out this manager's manager-review progress across their appraisees.
              // Each appraisee has managerReviewStatus === true once their manager review
              // is finished. We show "Manager Reviewed <done>/<total>" and only turn the
              // chip green when every appraisee has been reviewed.
              const appraisees = item.appraisees || [];
              const totalAppraisees = appraisees.length;
              const reviewedCount = appraisees.filter((a) => a.managerReviewStatus === true).length;
              const allReviewed = totalAppraisees > 0 && reviewedCount === totalAppraisees;
              return (
                <Paper
                  key={item.userId}
                  elevation={3}
                  sx={{
                    p: 1,
                    mt: 2,
                    border: selected?.userId === item?.userId ? 1 : '',
                    borderColor: '#004a77',
                    cursor: 'pointer',
                  }}
                  onClick={() => setSelected(item)}
                >
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Avatar
                      src={`https://workspace.teaminterval.net/assets/employee/photo/passport_size/${item.appraiserImage}.jpg`}
                    />
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Typography variant="subtitle1">{item.appraiserName}</Typography>
                    </Box>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                    <Chip
                      icon={
                        allReviewed ? (
                          <Icon icon="mdi:tick-all" width="18" height="18" />
                        ) : (
                          <Icon icon="bitcoin-icons:cross-filled" width="18" height="18" />
                        )
                      }
                      color={allReviewed ? 'success' : 'warning'}
                      variant="outlined"
                      size="small"
                      label={`Manager Reviewed ${reviewedCount}/${totalAppraisees}`}
                    />
                  </Box>
                </Paper>
              );
            })
          )}
        </Box>
      </Box>

      <Box sx={{ width: '60%', mt: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button sx={{ mb: 1 }} size="small" variant="outlined" onClick={() => setSelected(null)}>
            Clear
          </Button>
        </Box>
        <TableContainer component={Paper}>
          <Table>
            <TableHead sx={{ backgroundColor: '#f0f0f0' }}>
              <TableRow>
                <TableCell>User ID</TableCell>
                <TableCell>Employee</TableCell>
                <TableCell>Rating</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Manager Review</TableCell>
                <TableCell>Published</TableCell>
                <TableCell>Publish Report</TableCell>
                <TableCell>View Report</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {selected?.appraisees?.length > 0 ? (
                selected.appraisees.map((row) => (
                  <TableRow key={row.userId}>
                    <TableCell>{row.userId}</TableCell>
                    {console.log(row)}
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Avatar
                          src={`https://workspace.teaminterval.net/assets/employee/photo/passport_size/${row.employeeImage}.jpg`}
                          alt={row.employeeName}
                        />
                        <Typography>{row.employeeName}</Typography>
                      </Box>
                    </TableCell>
                    <TableCell>{row.rating ?? '—'}</TableCell>
                    <TableCell>{row.status}</TableCell>
                    <TableCell>{row.managerReviewStatus ? 'Completed' : 'Pending'}</TableCell>
                    <TableCell>{row.publishedStatus ? 'Published' : 'Unpublished'}</TableCell>
                    <TableCell>
                      <PublishReport cycleId={cycleId} id={row.appraisalCycleAssignmnentId} status={row.publishedStatus} disabled={row.managerReviewStatus === false} />
                    </TableCell>
                    <TableCell>
                      <AddEvaluationManagerFormLanding
                        userID={row?.userId}
                        id={cycleId}
                        title={cycleName}
                        viewOnly={true}
                        extraDisable={false}
                        isAdmin={true}
                      />
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    {selected ? 'No appraisees found.' : 'Select an appraiser to view details.'}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    </Box>
  );
};

export default ManageAppraisalCard;

const appraisers = {
  tab: {},
  data: [
    {
      userId: 100,
      appraiserName: 'JASEEM P ',
      appraiserImage: 'https://example.com/images/jaseem.jpg',
      completion: 0,
      appraisees: [
        {
          userId: 10,
          employeeName: 'Sharika P Jayan',
          employeeImage: 'https://example.com/images/sharika.jpg',
          rating: null,
          status: 'Pending',
          managerReviewStatus: false,
          publishedStatus: false,
        },
        {
          userId: 11,
          employeeName: 'test',
          employeeImage: 'https://example.com/images/sharika.jpg',
          rating: null,
          status: 'Pending',
          managerReviewStatus: false,
          publishedStatus: false,
        },
      ],
    },
    {
      userId: 101,
      appraiserName: 'Tesdtttt',
      appraiserImage: 'https://example.com/images/jaseem.jpg',
      completion: 0,
      appraisees: [
        {
          userId: 10,
          employeeName: 'testttt123',
          employeeImage: 'https://example.com/images/sharika.jpg',
          rating: null,
          status: 'Pending',
          managerReviewStatus: false,
          publishedStatus: false,
        },
        {
          userId: 11,
          employeeName: 'test1111111',
          employeeImage: 'https://example.com/images/sharika.jpg',
          rating: null,
          status: 'Pending',
          managerReviewStatus: false,
          publishedStatus: false,
        },
      ],
    },
    {
      userId: 102,
      appraiserName: 'Tesdtttt',
      appraiserImage: 'https://example.com/images/jaseem.jpg',
      completion: 0,
      appraisees: [
        {
          userId: 10,
          employeeName: 'testttt123',
          employeeImage: 'https://example.com/images/sharika.jpg',
          rating: null,
          status: 'Pending',
          managerReviewStatus: false,
          publishedStatus: false,
        },
        {
          userId: 11,
          employeeName: 'test1111111',
          employeeImage: 'https://example.com/images/sharika.jpg',
          rating: null,
          status: 'Pending',
          managerReviewStatus: false,
          publishedStatus: false,
        },
      ],
    },
    {
      userId: 103,
      appraiserName: 'Tesdtttt',
      appraiserImage: 'https://example.com/images/jaseem.jpg',
      completion: 0,
      appraisees: [
        {
          userId: 10,
          employeeName: 'testttt123',
          employeeImage: 'https://example.com/images/sharika.jpg',
          rating: null,
          status: 'Pending',
          managerReviewStatus: false,
          publishedStatus: false,
        },
        {
          userId: 11,
          employeeName: 'test1111111',
          employeeImage: 'https://example.com/images/sharika.jpg',
          rating: null,
          status: 'Pending',
          managerReviewStatus: false,
          publishedStatus: false,
        },
      ],
    },
    {
      userId: 104,
      appraiserName: 'Tesdtttt',
      appraiserImage: 'https://example.com/images/jaseem.jpg',
      completion: 0,
      appraisees: [
        {
          userId: 10,
          employeeName: 'testttt123',
          employeeImage: 'https://example.com/images/sharika.jpg',
          rating: null,
          status: 'Pending',
          managerReviewStatus: false,
          publishedStatus: false,
        },
        {
          userId: 11,
          employeeName: 'test1111111',
          employeeImage: 'https://example.com/images/sharika.jpg',
          rating: null,
          status: 'Pending',
          managerReviewStatus: false,
          publishedStatus: false,
        },
      ],
    },
    {
      userId: 105,
      appraiserName: 'Tesdtttt',
      appraiserImage: 'https://example.com/images/jaseem.jpg',
      completion: 0,
      appraisees: [
        {
          userId: 10,
          employeeName: 'testttt123',
          employeeImage: 'https://example.com/images/sharika.jpg',
          rating: null,
          status: 'Pending',
          managerReviewStatus: false,
          publishedStatus: false,
        },
        {
          userId: 11,
          employeeName: 'test1111111',
          employeeImage: 'https://example.com/images/sharika.jpg',
          rating: null,
          status: 'Pending',
          managerReviewStatus: false,
          publishedStatus: false,
        },
      ],
    },
    {
      userId: 106,
      appraiserName: 'Tesdtttt',
      appraiserImage: 'https://example.com/images/jaseem.jpg',
      completion: 0,
      appraisees: [
        {
          userId: 10,
          employeeName: 'testttt123',
          employeeImage: 'https://example.com/images/sharika.jpg',
          rating: null,
          status: 'Pending',
          managerReviewStatus: false,
          publishedStatus: false,
        },
        {
          userId: 11,
          employeeName: 'test1111111',
          employeeImage: 'https://example.com/images/sharika.jpg',
          rating: null,
          status: 'Pending',
          managerReviewStatus: false,
          publishedStatus: false,
        },
      ],
    },
    {
      userId: 107,
      appraiserName: 'Tesdtttt',
      appraiserImage: 'https://example.com/images/jaseem.jpg',
      completion: 0,
      appraisees: [
        {
          userId: 10,
          employeeName: 'testttt123',
          employeeImage: 'https://example.com/images/sharika.jpg',
          rating: null,
          status: 'Pending',
          managerReviewStatus: false,
          publishedStatus: false,
        },
        {
          userId: 11,
          employeeName: 'test1111111',
          employeeImage: 'https://example.com/images/sharika.jpg',
          rating: null,
          status: 'Pending',
          managerReviewStatus: false,
          publishedStatus: false,
        },
      ],
    },
    {
      userId: 108,
      appraiserName: 'Tesdtttt',
      appraiserImage: 'https://example.com/images/jaseem.jpg',
      completion: 0,
      appraisees: [
        {
          userId: 10,
          employeeName: 'testttt123',
          employeeImage: 'https://example.com/images/sharika.jpg',
          rating: null,
          status: 'Pending',
          managerReviewStatus: false,
          publishedStatus: false,
        },
        {
          userId: 11,
          employeeName: 'test1111111',
          employeeImage: 'https://example.com/images/sharika.jpg',
          rating: null,
          status: 'Pending',
          managerReviewStatus: false,
          publishedStatus: false,
        },
      ],
    },
    {
      userId: 109,
      appraiserName: 'Tesdtttt',
      appraiserImage: 'https://example.com/images/jaseem.jpg',
      completion: 0,
      appraisees: [
        {
          userId: 10,
          employeeName: 'testttt123',
          employeeImage: 'https://example.com/images/sharika.jpg',
          rating: null,
          status: 'Pending',
          managerReviewStatus: false,
          publishedStatus: false,
        },
        {
          userId: 11,
          employeeName: 'test1111111',
          employeeImage: 'https://example.com/images/sharika.jpg',
          rating: null,
          status: 'Pending',
          managerReviewStatus: false,
          publishedStatus: false,
        },
      ],
    },
    {
      userId: 110,
      appraiserName: 'Tesdtttt',
      appraiserImage: 'https://example.com/images/jaseem.jpg',
      completion: 0,
      appraisees: [
        {
          userId: 10,
          employeeName: 'testttt123',
          employeeImage: 'https://example.com/images/sharika.jpg',
          rating: null,
          status: 'Pending',
          managerReviewStatus: false,
          publishedStatus: false,
        },
        {
          userId: 11,
          employeeName: 'test1111111',
          employeeImage: 'https://example.com/images/sharika.jpg',
          rating: null,
          status: 'Pending',
          managerReviewStatus: false,
          publishedStatus: false,
        },
      ],
    },
  ],
};
